import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import axios, { type AxiosResponse, isAxiosError } from "axios";
import Cookies from "js-cookie";
import type { DateTime } from "luxon";
import type React from "react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient } from "../queryClient";
import { backendService } from "../services/backend.service";
import { biometricService } from "../services/biometric.service";
import { localStorageService } from "../services/LocalStorageService";
import { pushNotificationService } from "../services/pushNotification.service";
import { REFRESH_TOKEN_STORAGE_KEY, readRefreshToken, storageService } from "../services/storageService.service";
import type { AgencyIntegrationState } from "../utils/agencyIntegrations";
import { resetAgencySuppressionReports } from "../utils/agencySuppressionReports";
import { handleAnalyticsGlobalProperties, resetAnalyticsSession, trackErrorEvent, trackEvent } from "../utils/analytics";
import type { DepartmentWageConfig } from "../utils/DepartmentWage.config";
import type {
	AdminRole,
	AutoApproveRule,
	BreakRulesConfig,
	DepartmentLocation,
	DepartmentType,
	EmrIntegration,
	HoursRateConfig,
	HppdTarget,
	HrisIntegration,
	Job,
	Nurse,
	NurseRole,
	OvertimeConfig,
	PbjModel,
	RemoveUnionStaffWarning,
	SelfScheduling,
	ShiftType,
	StatsMode,
	UserRole,
	UserView,
} from "../utils/models";
import axiosWrapper, { resetRefreshState, setTokenDepartmentId, updateAccessToken } from "../utils/networkWrapper";
import { setCurrentViewMode } from "../utils/utilFunctions";
import { useSnackbar } from "./SnackbarContext";

const TERMS_VERSION = "2025-03-24";

export type Token =
	| {
			access_token: string;
			refresh_token?: string;
			terms_version?: string;
			/** The department the token opens; null when the server could not confirm one. */
			opened_department_id?: string | null;
			requires_mfa?: never;
			phone_number?: never;
			verify_email?: never;
			sub?: never;
	  }
	| {
			access_token?: never;
			refresh_token?: never;
			terms_version?: never;
			opened_department_id?: never;
			requires_mfa: boolean;
			phone_number: string;
			verify_email?: never;
			sub?: never;
	  }
	| {
			access_token?: never;
			refresh_token?: never;
			terms_version?: never;
			opened_department_id?: never;
			requires_mfa?: never;
			phone_number?: never;
			verify_email: boolean;
			sub: string;
	  };

export interface SpecialNurseConfig {
	useWageAsRate: boolean;
}

export interface DepartmentJobShiftTimes {
	jobId: string;
	partialBlockShifts: number[];
	fullShiftTimes: [start: number, end: number][];
}

export interface Facility {
	facility_id: string;
	name: string;
}

/**
 * A facility offered when creating a department, with the time zone a department joining it must use and
 * the inactivity windows it shares with every department in it.
 */
export interface FacilityOption extends Facility {
	timezone: string;
	nurse_inactivity_alert_days: number;
	nurse_inactive_after_days: number;
}

/** A facility and every department in it, for settings saved for the whole building. */
export interface FacilityWithDepartments extends Facility {
	departments: { department_id: string; display_name: string }[];
}

/** Which facility a department is created in: one that exists, or a new one. */
export type FacilityChoice = { kind: "existing"; facility_id: string } | { kind: "new"; name: string };

export interface Department {
	department_id: string;
	name: string;
	display_name: string;
	has_intermediate: boolean;
	hospital: string;
	facility_id: string;
	total_beds: number;
	target_ppn: number;
	timezone: string;
	start_date: DateTime;
	first_sunday: DateTime;
	locations: DepartmentLocation[];
	type: DepartmentType;
	notify_before_shift_start_minutes?: number;
	notify_before_shift_end_minutes?: number;
	/**
	 * Backward-compat scalar for pre-INH-3388 clients. Equal to the average of `hppd_target`.
	 * New code should consume `hppd_target` for per-day data.
	 */
	hppd: number;
	hppd_target: HppdTarget;
	ppd: number;
	min_valid_hppd_percent: number;
	max_valid_hppd_percent: number;
	roles: NurseRole[];
	partial_block_shifts: Partial<Record<`${NurseRole}` | "default", number[]>>;
	full_shift_times: Partial<Record<`${NurseRole}` | "default", [start: number, end: number][]>>;
	jobShiftTimes: DepartmentJobShiftTimes[];
	master_schedule_weeks: number;
	shift_types: ShiftType[];
	can_view_nurses: boolean;
	prn_cycle_duration_days: number;
	role_groups: Record<string, string[]>;
	hrisIntegrations?: HrisIntegration[];
	emrIntegrations?: EmrIntegration[]; // EMR integrations (was ~8 flat columns; now the `emr` table)
	nursa_facility_id?: string; // Added for Nursa support
	clipboard_facility_id?: string; // Added for Clipboard support
	/**
	 * One entry per agency this department is MAPPED to, carrying the on/off switch and its audit
	 * stamps (DAT-885). An agency that was never mapped is simply absent — absence and `active: false`
	 * are different states and must stay distinguishable. Optional because a cached `/user_info`
	 * payload predating PR3, and every test fixture, has no such key.
	 *
	 * Read-only on the client: the switch has server-side side effects (retracting open postings,
	 * subscribing the Nursa webhook) and moves through its own endpoint, never the config PUT.
	 */
	agencyIntegrations?: AgencyIntegrationState[];
	pbjModel?: PbjModel;
	masterScheduleV2Enabled: boolean;
	default_role?: NurseRole;
	special_nurses: Record<string, SpecialNurseConfig>;
	department_wage_config: DepartmentWageConfig;
	is_readonly: boolean;
	hide_wage: boolean;
	disable_edit_master_schedule: boolean;
	modify_user: boolean;
	adminRoles?: AdminRole[];
	allowed_holidays?: string[];
	holiday_boundaries: HolidayBoundary;
	day_shift_boundaries: DayShiftBoundary;
	weekend_boundaries: WeekendBoundary;
	week_start_day: number;
	hoursRateConfig: HoursRateConfig[];
	overtimeConfig?: OvertimeConfig[];
	breakRulesConfig?: BreakRulesConfig[];
	autoApproveRules?: AutoApproveRule[];
	isSynergyMode: boolean;
	selfScheduling?: SelfScheduling;
	statsMode: StatsMode;
	removeUnionStaffWarning: RemoveUnionStaffWarning;
	hasRecurringShift?: boolean;
	recurringPanelV2Enabled?: boolean;
	hasLocationConflictWarning?: boolean;
	enableShiftBonuses?: boolean;
	enableAutoRefresh?: boolean;
	enableSwap?: boolean;
	draftModeEnabled?: boolean;
	hideOverstaffingWarning?: boolean;
	countBedholdInCensus?: boolean;
	/** Facility-local hours at which census is read from ADT. `null`/absent = the feature is off. */
	censusIntervals?: number[] | null;
	agencyScheduledWithoutPunchDays?: number;
	combineConsecutiveAgencyShifts?: boolean;
	agencyPostDelayDefaultHours?: number;
	units?: string[];
	floors?: string[];
	callOffBypassHoursBeforeShiftBedside?: number;
	callOffBypassHoursAfterStartBedside?: number;
	callOffBypassHoursBeforeShiftAdmin?: number;
	callOffBypassHoursAfterStartAdmin?: number;
	callOffMessageLeadTimeMinutes?: number | null;
	/** Days before a newly opened shift that predicted-OT staff become eligible. `null` preserves existing eligibility. */
	overtimeEligibilityReleaseDays?: number | null;
	/**
	 * Hours before a newly opened shift that predicted-OT staff become eligible — the source of truth
	 * (the backend stores a single hours column). `overtimeEligibilityReleaseDays` above is retained as
	 * the legacy `hours // 24` mirror for older clients. `null` preserves existing eligibility.
	 */
	overtimeEligibilityReleaseHours?: number | null;
	staffScheduleDisplayUntilDate?: DateTime;
	scheduleCycleWeeks?: number;
	schedulePublishLeadWeeks?: number;
	departmentDataHistoryWeeks: number;
	autoCreateScheduleWeeksAhead: number;
	late_arrival_minutes: number;
	early_departure_minutes: number;
	shift_delta_minutes: number;
	/** Day-start grace for SCHEDULED shifts. shift_delta_minutes is the punch-in buffer. 0 turns it off. */
	scheduled_shift_delta_minutes: number;
	shift_delta_threshold_hours: number;
	dayStart: string;
	/** DAT-527: payroll/OT clock, "HH:MM:SS" like `dayStart`. null = never decoupled, OT uses `dayStart`. */
	payrollStartTime: string | null;
	hrisPositions?: string[];
	jobIds?: string[];
	longShiftDuration?: number;
	jobs?: Job[];
	nurse_inactivity_alert_days?: number | null;
	nurse_inactive_after_days?: number | null;
}

export interface HolidayBoundary {
	startHour: number;
	endHour: number;
}

export interface DayShiftBoundary {
	startHour: number;
	endHour: number;
}

export interface WeekendBoundary {
	startDay: number;
	startHour: number;
	endDay: number;
	endHour: number;
}
export interface UserNurseInfo {
	nurse: Nurse;
	departments: string[];
}

export interface UserInfo {
	id: string;
	username: string;
	image: string;
	departments: Department[];
	requires_mfa: boolean;
	phone_number: string;
	first_name: string;
	role: string;
	nurses_info: UserNurseInfo[];
	support: boolean;
	modify_user: boolean;
	impersonated_by?: string | null;
	terms_version?: string;
	hide_wage: boolean;
	disable_edit_master_schedule: boolean;
	is_read_only: boolean;
	push_notification_token?: string | null;
	push_notification_platform?: string | null;
	language?: string;
	group_floatpool_shifts: boolean;
	created_at: DateTime;
	/** Which surface the current token is for. From userInfo, never decoded from the JWT. */
	view?: UserView;
	/** Departments the user is a HeadNurse in. */
	adminDepartmentIds?: string[];
	/** Departments the user has an active nurse record in. */
	staffDepartmentIds?: string[];
	/** The facilities of the user's departments, for the header's facility picker. */
	facilities: Facility[];
}

type LoginAtteptResult = { isSuccessful: boolean; mfa: boolean };
interface AuthContextType {
	userToken: Token | undefined;
	userInfo: UserInfo;
	isLoggedIn: boolean;
	isCheckingAuth: boolean;
	mfa: boolean;
	email: string;
	maskedPhone: string;
	showTermsDialog: boolean;
	setShowTermsDialog: (show: boolean) => void;
	login: (username: string, password: string) => Promise<LoginAtteptResult>;
	saveTokenData: (token: Token) => Promise<LoginAtteptResult>;
	installDepartmentSession: (token: Token, departmentId: string, departmentUserInfo: UserInfo) => void;
	verifyCode: (email: string, code: string) => Promise<LoginAtteptResult>;
	logout: () => Promise<void>;
	impersonateUser: (token: string) => void;
	stopImpersonation: () => Promise<void>;
	acceptTerms: () => Promise<void>;
	updateUserInfo: () => Promise<UserInfo | undefined>;
}

interface AuthProviderProps {
	children: ReactNode;
}

export let accessToken: any;

export type RefreshResult =
	| { kind: "success"; accessToken: string; departmentId: string | null }
	| { kind: "no_token" }
	| { kind: "rejected"; status: number }
	| { kind: "transient"; status?: number; message?: string; reason?: "read_failed" };

interface RefreshResponseBody {
	access_token?: string;
}

export const refreshAccessToken = async (): Promise<RefreshResult> => {
	const department_id = localStorageService.getChosenDepartmentID();
	// Re-send the view: /refresh can't recover it otherwise, and the backend re-validates it anyway.
	const view = localStorageService.getChosenView();
	const params = new URLSearchParams();
	if (department_id) params.set("department_id", department_id);
	if (view) params.set("view", view);
	const url = params.size > 0 ? `/refresh?${params.toString()}` : "/refresh";

	try {
		let response: AxiosResponse<RefreshResponseBody>;
		if (Capacitor.isNativePlatform()) {
			// A failed read must not look like "no token" — that would delete a token that's still there.
			// Treat a read failure as transient; only a confirmed empty read is no_token.
			const { value: refreshToken, readFailed } = await readRefreshToken();
			if (readFailed) {
				trackEvent("refresh no_token reason", { reason: "read_failed" });
				return { kind: "transient", reason: "read_failed", message: "refresh_token read failed" };
			}
			if (!refreshToken) {
				trackEvent("refresh no_token reason", { reason: "absent" });
				return { kind: "no_token" };
			}

			response = await axiosWrapper.post<RefreshResponseBody>(url, {
				refresh_token: refreshToken,
			});
		} else {
			// Web: use cookies (withCredentials)
			const is_authenticated = Cookies.get("is_authenticated");
			if (!is_authenticated) {
				trackEvent("refresh no_token reason", { reason: "web_cookie_absent" });
				return { kind: "no_token" };
			}

			response = await axiosWrapper.post<RefreshResponseBody>(url);
		}

		const newAccessToken = response.data?.access_token;
		// Treat a 2xx with no access_token as transient — never let the caller install
		// an undefined Bearer header, which would silently mint a broken session.
		if (!newAccessToken) {
			trackEvent("refresh_token malformed response", { status: response.status });
			return { kind: "transient", status: response.status, message: "missing access_token in refresh response" };
		}
		return { kind: "success", accessToken: newAccessToken, departmentId: department_id };
	} catch (error) {
		const status = isAxiosError(error) ? error.response?.status : undefined;
		const message = error instanceof Error ? error.message : undefined;

		const isAuthRejection = status === 401 || status === 403;
		if (isAuthRejection) {
			trackEvent("refresh_token rejected", { status, message });
			if (Capacitor.isNativePlatform()) {
				await storageService.remove(REFRESH_TOKEN_STORAGE_KEY);
			}
			return { kind: "rejected", status: status as number };
		}
		trackEvent("refresh_token transient failure", { status, message });
		return { kind: "transient", status, message };
	}
};

/**
 * Exported so a `/dev/*` fixture can re-provide it over the real one and render ROLE-GATED UI — the
 * support-only half of a control that is otherwise only reachable on a support account, which is
 * exactly the combination those fixtures exist to make visible (`HospitalDataContext` is exported
 * for the same reason). Everything in `src/` outside a fixture should still go through `useAuth`,
 * which throws when the provider is missing instead of handing back `undefined`.
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
	const { t } = useTranslation();
	const [userToken, setUserToken] = useState<Token | undefined>();
	const [userInfo, setUserInfoState] = useState<UserInfo>({} as UserInfo);
	const [isCheckingAuth, setIsCheckingAuth] = useState(true);
	const [mfa, setMfa] = useState(false);
	const [email, setEmail] = useState("");
	const [maskedPhone, setMaskedPhone] = useState("");
	const [showTermsDialog, setShowTermsDialog] = useState(false);
	const { showSnackbar } = useSnackbar();
	const sessionGenerationRef = useRef(0);
	const nativeTokenStorageQueueRef = useRef<Promise<void>>(Promise.resolve());
	const logoutInProgressRef = useRef(false);
	const activeLogoutCountRef = useRef(0);

	// The only writer of userInfo, so it's also the only writer of the module-level view store
	// (setCurrentViewMode), kept in sync before every render that reads it.
	const setUserInfo = useCallback((newUserInfo: UserInfo) => {
		setCurrentViewMode(newUserInfo.view);
		// Persisted too: the in-memory store dies on reload, and /refresh needs this to restore the view.
		if (newUserInfo.view) {
			localStorageService.setChosenView(newUserInfo.view);
		} else {
			localStorageService.removeChosenView();
		}
		setUserInfoState(newUserInfo);
	}, []);

	const isLoggedIn = useMemo(() => {
		// If we have userInfo, we're definitely logged in
		if (userInfo.id) return true;

		// If we're still checking auth, don't say we're logged out yet
		if (isCheckingAuth) return false;

		return !!userToken?.access_token;
	}, [userInfo.id, isCheckingAuth, userToken]);

	const updateUserInfo = useCallback(async () => {
		if (userToken?.access_token) {
			try {
				const { data: userData } = await backendService.getUserInfo();
				setUserInfo(userData);
				handleAnalyticsGlobalProperties(userData);
				return userData;
			} catch (error) {
				console.error("Error fetching user info:", error);
				showSnackbar(t("error.fetching.user.info"), "error");
			}
		}
	}, [userToken?.access_token, showSnackbar, t, setUserInfo]);

	useEffect(() => {
		if (isLoggedIn) {
			// Don't show terms dialog if user is impersonated
			if (userInfo?.impersonated_by) {
				setShowTermsDialog(false);
				return;
			}

			// Check if user needs to see terms dialog based on terms version
			const userTermsVersion = userToken && "terms_version" in userToken ? userToken.terms_version : userInfo?.terms_version;

			if (!userTermsVersion || userTermsVersion < TERMS_VERSION) {
				setShowTermsDialog(true);
			}
		}
	}, [isLoggedIn, userToken, userInfo]);

	const impersonateUser = async (token: string) => {
		try {
			// Write-once while impersonating, so re-impersonating (the view switch) keeps the support
			// token. Starting fresh overwrites, so a leftover from an earlier session is never adopted.
			localStorageService.storeOriginalTokenOnce(userToken?.access_token, Boolean(userInfo?.impersonated_by));

			// Set the new token and update auth headers
			localStorage.setItem("impersonation_token", token);
			sessionGenerationRef.current += 1;

			// Update auth headers
			axios.defaults.headers.common.Authorization = `Bearer ${token}`;
			axiosWrapper.defaults.headers.common.Authorization = `Bearer ${token}`;

			// Set is_authenticated cookie to maintain session
			Cookies.set("is_authenticated", "True", { expires: 30 });

			// Fetch user info with new token
			const { data: userData } = await backendService.getUserInfo();

			// Update all states at once to avoid race conditions
			setUserToken({ access_token: token });
			setUserInfo(userData);
			handleAnalyticsGlobalProperties(userData);

			// Wait for state updates before redirecting
			await new Promise((resolve) => setTimeout(resolve, 100));

			// Clear any existing intervals to prevent race conditions
			const intervalId = localStorage.getItem("tokenRefreshInterval");
			if (intervalId) {
				clearInterval(Number(intervalId));
				localStorage.removeItem("tokenRefreshInterval");
			}

			// Redirect to home page with timestamp to force reload
			window.location.href = `/?ts=${Date.now()}`;
		} catch (error) {
			console.error("Error during impersonation:", error);
			// Revert changes if something goes wrong
			const originalToken = localStorage.getItem("originalToken");
			if (originalToken) {
				setUserToken({ access_token: originalToken });
				axios.defaults.headers.common.Authorization = `Bearer ${originalToken}`;
				axiosWrapper.defaults.headers.common.Authorization = `Bearer ${originalToken}`;
			}
			localStorage.removeItem("impersonation_token");
			localStorage.removeItem("originalToken");
			throw error;
		}
	};

	const stopImpersonation = async () => {
		try {
			const chosenDeptId = localStorageService.getChosenDepartmentID();

			// Call backend endpoint to stop impersonation and get new token for original user
			const response = await backendService.stopImpersonation(chosenDeptId);
			const newToken = response.data.access_token;
			sessionGenerationRef.current += 1;

			// Update auth headers with new token
			axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
			axiosWrapper.defaults.headers.common.Authorization = `Bearer ${newToken}`;

			// Fetch user info with new token
			const { data: userData } = await backendService.getUserInfo();

			// Update all states at once
			setUserToken({ access_token: newToken });
			setUserInfo(userData);
			handleAnalyticsGlobalProperties(userData);

			// Clear impersonation data from localStorage
			localStorageService.removeImpersonationToken();
			localStorageService.removeOriginalToken();

			// Wait for state updates before redirecting
			await new Promise((resolve) => setTimeout(resolve, 100));

			// Clear any existing intervals
			const intervalId = localStorage.getItem("tokenRefreshInterval");
			if (intervalId) {
				clearInterval(Number(intervalId));
				localStorage.removeItem("tokenRefreshInterval");
			}

			// Redirect to home page with timestamp to force reload
			window.location.href = `/?ts=${Date.now()}`;
		} catch (error) {
			console.error("Error during stop impersonation:", error);
			throw error;
		}
	};

	const acceptTerms = async () => {
		try {
			await axiosWrapper.post("/users/accept-terms", {
				terms_version_date: TERMS_VERSION,
			});
			setShowTermsDialog(false);
		} catch (error) {
			console.error("Error accepting terms:", error);
			showSnackbar("Failed to accept terms and conditions", "error");
		}
	};

	/** Native keeps its refresh token in secure storage, since it has no http-only cookie to carry it. */
	const persistNativeTokens = async (token: Token, path: "saveTokenData" | "installDepartmentSession") => {
		if (!Capacitor.isNativePlatform()) return;
		if (token.refresh_token) {
			try {
				const stored = await storageService.set(REFRESH_TOKEN_STORAGE_KEY, token.refresh_token);
				if (!stored) trackEvent("refresh_token store failed", { path });
			} catch (error) {
				trackEvent("refresh_token store failed", { path, error: (error as Error).message });
				console.error("Failed to store refresh_token:", error);
			}
		} else {
			// Native client got a response with no refresh_token to persist → can't silently resume.
			trackEvent("native login without refresh_token in body");
		}
	};

	const updateBiometricToken = async (token: Token) => {
		if (!Capacitor.isNativePlatform()) return;
		try {
			// Never send the token itself: trackEvent fans every property out to Mixpanel, GA and
			// Stonly, where a live bearer token would sit readable for its whole lifetime. Presence and
			// length are what these events are actually for, and match the "Token received" event below.
			trackEvent("updateToken", {
				action: "updateToken",
				hasAccessToken: !!token.access_token,
				accessTokenLength: token.access_token?.length,
			});
			await biometricService.updateToken(token.access_token || "");
			trackEvent("updateToken success", {
				action: "updateToken success",
				hasAccessToken: !!token.access_token,
				accessTokenLength: token.access_token?.length,
			});
		} catch (error) {
			trackEvent("Failed to update biometric token", {
				action: "Failed to update biometric token",
				error: error,
			});
			console.error("Failed to update biometric token:", error);
			trackErrorEvent("Failed to update biometric token", error, {
				action: "Failed to update biometric token",
			});
		}
	};

	const persistNativeSession = (token: Token, path: "saveTokenData" | "installDepartmentSession"): Promise<void> => {
		if (!Capacitor.isNativePlatform()) return Promise.resolve();
		const generation = sessionGenerationRef.current;
		// A logout queues removal behind every started write; an older queued session cannot write afterward.
		const persistence = nativeTokenStorageQueueRef.current
			.then(async () => {
				if (generation !== sessionGenerationRef.current || logoutInProgressRef.current) return;
				await persistNativeTokens(token, path);
				if (generation !== sessionGenerationRef.current || logoutInProgressRef.current) return;
				await updateBiometricToken(token);
			})
			.catch((error) => {
				console.error("Failed to persist native session:", error);
			});
		nativeTokenStorageQueueRef.current = persistence;
		return persistence;
	};

	const installAccessToken = (token: Token, departmentId: string | null) => {
		// Every installed token invalidates async work started under the previous one.
		sessionGenerationRef.current += 1;
		Cookies.set("is_authenticated", "True", { expires: 30 });
		// ensures subsequent API calls use the new token
		if (token.access_token) {
			updateAccessToken(token.access_token, departmentId);
			axios.defaults.headers.common.Authorization = `Bearer ${token.access_token}`;
		}
		return sessionGenerationRef.current;
	};

	const saveTokenData = async (token: Token): Promise<LoginAtteptResult> => {
		if (logoutInProgressRef.current) return { isSuccessful: false, mfa: false };
		// DEFAULT: a response that confirms no department leaves it unknown, so the first department this
		// tab shows mints its own token rather than trust a guess.
		const generation = installAccessToken(token, token.opened_department_id ?? null);
		await persistNativeSession(token, "saveTokenData");
		if (generation !== sessionGenerationRef.current || logoutInProgressRef.current) return { isSuccessful: false, mfa: false };
		setUserToken(token);
		const { data: newUserInfo } = await backendService.getUserInfo();
		if (generation !== sessionGenerationRef.current || logoutInProgressRef.current) return { isSuccessful: false, mfa: false };
		setUserInfo(newUserInfo);
		handleAnalyticsGlobalProperties(newUserInfo);

		return { isSuccessful: true, mfa: false };
	};

	/**
	 * Installs a department's token together with the user info fetched for it, without awaiting in
	 * between — so the caller can switch the department on screen in the same render. Native
	 * persistence is queued before logout removal; both of its steps report their own failures.
	 */
	const installDepartmentSession = (token: Token, departmentId: string, departmentUserInfo: UserInfo) => {
		if (logoutInProgressRef.current) return;
		installAccessToken(token, departmentId);
		setUserToken(token);
		setUserInfo(departmentUserInfo);
		handleAnalyticsGlobalProperties(departmentUserInfo);
		void persistNativeSession(token, "installDepartmentSession");
	};

	const verifyCode = async (email: string, code: string): Promise<LoginAtteptResult> => {
		const generation = sessionGenerationRef.current;
		try {
			const department_id = localStorageService.getChosenDepartmentID();
			const url = department_id ? `/verify-sms?department_id=${department_id}` : "/verify-sms";
			const { data: token } = await axiosWrapper.post<Token>(url, {
				email,
				code,
			});

			if (generation !== sessionGenerationRef.current) return { isSuccessful: false, mfa: false };
			return await saveTokenData(token);
		} catch (error) {
			showSnackbar("Login error", "error");
			return { isSuccessful: false, mfa: false };
		}
	};

	const login = async (username: string, password: string): Promise<LoginAtteptResult> => {
		const generation = sessionGenerationRef.current;
		try {
			const searchParams = new URLSearchParams({
				username,
				password,
				grant_type: "password",
			});
			const chosenDeptId = localStorageService.getChosenDepartmentID();
			const tokenUrl = chosenDeptId ? `/token?department_id=${chosenDeptId}` : "/token";
			const { data: token } = await axiosWrapper.post<Token>(tokenUrl, searchParams);
			trackEvent("Token received", {
				hasAccessToken: !!token.access_token,
				accessTokenLength: token.access_token?.length,
				termsVersion: token.terms_version,
				requiresMfa: token.requires_mfa,
				isNative: Capacitor.isNativePlatform(),
			});

			if (generation !== sessionGenerationRef.current) return { isSuccessful: false, mfa: false };
			if (token.requires_mfa) {
				trackEvent("requires_mfa", {
					phone_number: token.phone_number,
					username,
				});
				setMfa(true);
				setEmail(username);
				setMaskedPhone(token.phone_number);

				return { isSuccessful: false, mfa: true };
			}

			return await saveTokenData(token);
		} catch (error) {
			trackEvent("login error", {
				errorMessage: (error as Error).message,
			});
			console.error(error);
			let errorMessage = t("error.login");
			const errorDetail = (error as any)?.response?.data?.detail;

			if (typeof errorDetail === "string") {
				try {
					const parsed = JSON.parse(errorDetail);
					const isUIVisibleError = parsed?.error_type === "UIVisibleError";
					if (isUIVisibleError) {
						const [key, ...params] = parsed.message.split("|");
						if (params.length > 0) {
							const status = params[0];
							errorMessage = t(key, {
								status: t(`status.${status.toLowerCase()}`),
							});
						} else {
							errorMessage = t(parsed.message);
						}
					} else {
						errorMessage = t("error.login");
					}
				} catch {}
			} else if (errorDetail?.message) {
				// If error_type is UIVisibleError, translate the message
				if (errorDetail?.error_type === "UIVisibleError") {
					const [key, ...params] = errorDetail.message.split("|");
					if (params.length > 0) {
						const status = params[0];
						errorMessage = t(key, { status: t(`status.${status.toLowerCase()}`) });
					} else {
						errorMessage = t(errorDetail.message);
					}
				} else {
					errorMessage = errorDetail.message;
				}
			}

			showSnackbar(errorMessage, "error");
			return { isSuccessful: false, mfa: false };
		}
	};

	// In-memory clear only — no token/cookie removal, push cleanup, or /logout. For when there's no session to end.
	function clearSessionUiState() {
		setUserToken(undefined);
		setUserInfo({} as UserInfo);
		setMfa(false);
	}

	async function logout(reason: "explicit" | "rejected" = "explicit") {
		// Mark the session ended before cleanup's first await, then remove after all earlier native writes.
		activeLogoutCountRef.current += 1;
		logoutInProgressRef.current = true;
		sessionGenerationRef.current += 1;
		resetRefreshState();
		const removeNativeRefreshToken = Capacitor.isNativePlatform()
			? nativeTokenStorageQueueRef.current.then(() => storageService.remove(REFRESH_TOKEN_STORAGE_KEY))
			: Promise.resolve();
		// Keep the queue usable, while the original promise still reports removal failure to logout.
		nativeTokenStorageQueueRef.current = removeNativeRefreshToken.catch((error) => {
			console.error("Failed to remove native refresh token:", error);
		});
		try {
			trackEvent("logout", { reason, platform: Capacitor.getPlatform() });
			// Clear cookies
			Cookies.remove("is_authenticated");

			// Clear secure storage (for mobile)
			if (Capacitor.isNativePlatform()) {
				try {
					// Clean up push notifications
					await pushNotificationService.cleanup();
				} catch (error) {
					console.error("Error cleaning up push notifications:", error);
				}
			}
			const [removalResult] = await Promise.allSettled([removeNativeRefreshToken]);

			// Clear all stored tokens and user info
			localStorage.removeItem("impersonation_token");
			localStorage.removeItem("originalToken");
			localStorageService.removeChosenDepartmentID();
			// Cleared with the department so a stale view isn't re-sent for the next user.
			localStorageService.removeChosenView();

			setUserToken(undefined);
			setUserInfo({} as UserInfo);
			setMfa(false);

			// Refresh analytics on the way out so a different user logging in on the same tab starts a fresh
			// session replay and event identity instead of inheriting this user's. The agency-suppression
			// bookkeeping is once-per-session by design, so it is reset here for exactly the same reason.
			resetAnalyticsSession();
			resetAgencySuppressionReports();
			// Same for cached API data: a CV fetched for this user must not be served to the next one.
			queryClient.clear();

			// Refresh was abandoned before cleanup's first await. /logout is unauthenticated, so its
			// response cannot start a new refresh for the session we are ending.
			await axiosWrapper.post("/logout");
			if (removalResult.status === "rejected") throw removalResult.reason;
		} finally {
			activeLogoutCountRef.current -= 1;
			logoutInProgressRef.current = activeLogoutCountRef.current > 0;
		}
	}

	async function getNewAccessToken() {
		if (logoutInProgressRef.current) return;
		const generation = sessionGenerationRef.current;
		setIsCheckingAuth(true);
		try {
			// Check for impersonation token first
			const impersonationToken = localStorage.getItem("impersonation_token");

			if (impersonationToken) {
				sessionGenerationRef.current += 1;
				const installedGeneration = sessionGenerationRef.current;
				// Update auth headers first
				axios.defaults.headers.common.Authorization = `Bearer ${impersonationToken}`;
				axiosWrapper.defaults.headers.common.Authorization = `Bearer ${impersonationToken}`;

				try {
					const { data: userData } = await backendService.getUserInfo();
					if (installedGeneration !== sessionGenerationRef.current || logoutInProgressRef.current) return;

					// Update all states at once
					setUserToken({ access_token: impersonationToken });
					setUserInfo(userData);
					handleAnalyticsGlobalProperties(userData);
					setIsCheckingAuth(false);
					return;
				} catch (error) {
					console.error("Error fetching impersonated user info:", error);
					// If we can't fetch user info, clear impersonation and try normal token
					localStorage.removeItem("impersonation_token");
					localStorage.removeItem("originalToken");
					throw error;
				}
			}

			const result = await refreshAccessToken();
			if (generation !== sessionGenerationRef.current || logoutInProgressRef.current) return;
			if (result.kind === "success") {
				sessionGenerationRef.current += 1;
				const installedGeneration = sessionGenerationRef.current;
				axios.defaults.headers.common.Authorization = `Bearer ${result.accessToken}`;
				axiosWrapper.defaults.headers.common.Authorization = `Bearer ${result.accessToken}`;
				setTokenDepartmentId(result.departmentId);

				const { data: userData } = await backendService.getUserInfo();
				if (installedGeneration !== sessionGenerationRef.current || logoutInProgressRef.current) return;

				setUserToken({ access_token: result.accessToken });
				setUserInfo(userData);
				handleAnalyticsGlobalProperties(userData);
			} else if (result.kind === "rejected") {
				// Genuine 401/403: token already invalidated/removed — full teardown is correct.
				await logout("rejected");
			} else if (result.kind === "no_token") {
				// No session to end — show login without the destructive logout() teardown.
				clearSessionUiState();
			} else if (result.kind === "transient" && !result.status && result.reason !== "read_failed") {
				// No HTTP status means the request never reached the server — pure network failure.
				showSnackbar(t("error.no.internet.connection"), "error");
			}
			// Other transient (5xx / timeout): leave session state untouched.
			// The network interceptor will retry refresh on the next request and recover.
		} catch (error) {
			// Downstream errors (e.g. getUserInfo) must not tear down the session;
			// only an explicit auth rejection from refreshAccessToken triggers logout.
			console.error("Error in getNewAccessToken:", error);
		} finally {
			setIsCheckingAuth(false);
		}
	}
	useEffect(() => {
		if (!window.location.pathname.includes("reset-password")) {
			getNewAccessToken();
		} else {
			// If we're on reset-password page, we're not checking auth
			setIsCheckingAuth(false);
		}
	}, []);

	// Keep a ref so the appStateChange listener can read the current value
	// without needing to be re-registered on every token change.
	const userTokenRef = useRef(userToken);
	useEffect(() => {
		userTokenRef.current = userToken;
	}, [userToken]);

	// Re-run the token refresh whenever the app returns to foreground without
	// an active session. Covers the case where a 5xx / network failure during
	// the initial check left the user on the login screen while the app sat in
	// the background — the single mount-time effect never fires again on resume.
	useEffect(() => {
		if (!Capacitor.isNativePlatform()) return;

		const listener = CapacitorApp.addListener("appStateChange", ({ isActive }) => {
			if (!isActive || userTokenRef.current) return;
			getNewAccessToken();
		});

		return () => {
			listener.then((handle) => handle.remove());
		};
	}, []);

	useEffect(() => {
		accessToken = userToken?.access_token;
	}, [userToken]);

	return (
		<AuthContext.Provider
			value={{
				userToken,
				userInfo,
				isLoggedIn,
				isCheckingAuth,
				mfa,
				email,
				maskedPhone,
				showTermsDialog,
				setShowTermsDialog,
				saveTokenData,
				installDepartmentSession,
				login,
				verifyCode,
				logout,
				impersonateUser,
				stopImpersonation,
				acceptTerms,
				updateUserInfo,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
